import React, { useState } from 'react';
import { PitchDeckGenerationOptions } from '../../types/pitchDeck';
import { BusinessPlan } from '../../lib/supabase';
import { toast } from 'react-toastify';

interface PitchDeckCreationModalProps {
  businessPlans: BusinessPlan[];
  onClose: () => void;
  onDeckCreated: (deckId: string) => void;
  isGenerating?: boolean;
  
  // Replace Zustand store functions with prop functions
  createNewDeck?: (title: string) => Promise<string>;
  generateDeckFromBusinessPlan?: (businessPlanId: string, options?: PitchDeckGenerationOptions) => Promise<string>;
  generateDeckFromScratch?: (options: PitchDeckGenerationOptions) => Promise<string>;
}

export const PitchDeckCreationModal: React.FC<PitchDeckCreationModalProps> = ({ 
  businessPlans,
  onClose,
  onDeckCreated,
  isGenerating = false,
  createNewDeck,
  generateDeckFromBusinessPlan,
  generateDeckFromScratch
}) => {
  // Modal tabs
  const [activeTab, setActiveTab] = useState<'blank' | 'business-plan' | 'ai'>('blank');
  
  // Form state
  const [title, setTitle] = useState('');
  const [selectedBusinessPlanId, setSelectedBusinessPlanId] = useState<string>('');
  const [aiOptions, setAiOptions] = useState<PitchDeckGenerationOptions>({
    title: '',
    industry: '',
    targetAudience: '',
    keyPoints: [''],
    tone: 'professional',
    slideCount: 10
  });
  
  // Local loading state
  const [loading, setLoading] = useState(false);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      let deckId: string | undefined;
      
      switch (activeTab) {
        case 'blank':
          if (createNewDeck) {
            deckId = await createNewDeck(title);
          } else {
            throw new Error('createNewDeck function not provided');
          }
          break;
        case 'business-plan':
          if (!selectedBusinessPlanId) {
            toast.error('Please select a business plan');
            setLoading(false);
            return;
          }
          if (generateDeckFromBusinessPlan) {
            deckId = await generateDeckFromBusinessPlan(selectedBusinessPlanId);
          } else {
            throw new Error('generateDeckFromBusinessPlan function not provided');
          }
          break;
        case 'ai':
          if (generateDeckFromScratch) {
            deckId = await generateDeckFromScratch({
              ...aiOptions,
              title: aiOptions.title || title
            });
          } else {
            throw new Error('generateDeckFromScratch function not provided');
          }
          break;
      }
      
      setLoading(false);
      if (deckId) {
        onDeckCreated(deckId);
        onClose();
      }
    } catch (error) {
      setLoading(false);
      console.error('Error creating pitch deck:', error);
      toast.error('Failed to create pitch deck');
    }
  };
  
  // Handle adding a new key point
  const handleAddKeyPoint = () => {
    setAiOptions(prev => ({
      ...prev,
      keyPoints: [...(prev.keyPoints || []), '']
    }));
  };
  
  // Handle updating a key point
  const handleKeyPointChange = (index: number, value: string) => {
    const updatedKeyPoints = [...(aiOptions.keyPoints || [])];
    updatedKeyPoints[index] = value;
    
    setAiOptions(prev => ({
      ...prev,
      keyPoints: updatedKeyPoints
    }));
  };
  
  // Handle removing a key point
  const handleRemoveKeyPoint = (index: number) => {
    setAiOptions(prev => ({
      ...prev,
      keyPoints: prev.keyPoints ? prev.keyPoints.filter((_, i) => i !== index) : []
    }));
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Create New Pitch Deck</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl">&times;</span>
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button 
              className={`px-4 py-2 font-medium ${
                activeTab === 'blank' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('blank')}
            >
              Blank Deck
            </button>
            <button 
              className={`px-4 py-2 font-medium ${
                activeTab === 'business-plan' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('business-plan')}
            >
              From Business Plan
            </button>
            <button 
              className={`px-4 py-2 font-medium ${
                activeTab === 'ai' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('ai')}
            >
              AI Generated
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Blank Deck Form */}
            {activeTab === 'blank' && (
              <div className="mb-4">
                <label htmlFor="title" className="block text-gray-700 font-medium mb-2">
                  Deck Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="Enter a title for your pitch deck"
                />
              </div>
            )}
            
            {/* From Business Plan Form */}
            {activeTab === 'business-plan' && (
              <>
                <div className="mb-4">
                  <label htmlFor="businessPlan" className="block text-gray-700 font-medium mb-2">
                    Select Business Plan
                  </label>
                  {businessPlans.length === 0 ? (
                    <p className="text-gray-500">No business plans available. Create a business plan first.</p>
                  ) : (
                    <select
                      id="businessPlan"
                      value={selectedBusinessPlanId}
                      onChange={(e) => setSelectedBusinessPlanId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select a business plan</option>
                      {businessPlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.title || `Business Plan for ${plan.idea_id}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                <p className="text-gray-600 text-sm mb-4">
                  A pitch deck will be automatically generated based on your selected business plan.
                  You'll be able to customize it after generation.
                </p>
              </>
            )}
            
            {/* AI Generation Form */}
            {activeTab === 'ai' && (
              <>
                <div className="mb-4">
                  <label htmlFor="ai-title" className="block text-gray-700 font-medium mb-2">
                    Pitch Deck Title
                  </label>
                  <input
                    type="text"
                    id="ai-title"
                    value={aiOptions.title}
                    onChange={(e) => setAiOptions({...aiOptions, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter a title for your pitch deck"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="industry" className="block text-gray-700 font-medium mb-2">
                    Industry
                  </label>
                  <input
                    type="text"
                    id="industry"
                    value={aiOptions.industry}
                    onChange={(e) => setAiOptions({...aiOptions, industry: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Technology, Healthcare, Finance"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="targetAudience" className="block text-gray-700 font-medium mb-2">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    id="targetAudience"
                    value={aiOptions.targetAudience}
                    onChange={(e) => setAiOptions({...aiOptions, targetAudience: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Investors, Customers, Partners"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    Key Points
                  </label>
                  {(aiOptions.keyPoints || []).map((point, index) => (
                    <div key={index} className="flex items-center mb-2">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => handleKeyPointChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Key point ${index + 1}`}
                        required
                      />
                      {(aiOptions.keyPoints || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyPoint(index)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <span className="text-xl">&times;</span>
                        </button>
                      )}
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={handleAddKeyPoint}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-1"
                  >
                    + Add another key point
                  </button>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="tone" className="block text-gray-700 font-medium mb-2">
                    Presentation Tone
                  </label>
                  <select
                    id="tone"
                    value={aiOptions.tone}
                    onChange={(e) => setAiOptions({
                      ...aiOptions, 
                      tone: e.target.value as PitchDeckGenerationOptions['tone']
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="enthusiastic">Enthusiastic</option>
                    <option value="technical">Technical</option>
                    <option value="minimalist">Minimalist</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="slideCount" className="block text-gray-700 font-medium mb-2">
                    Number of Slides
                  </label>
                  <input
                    type="number"
                    id="slideCount"
                    min={5}
                    max={20}
                    value={aiOptions.slideCount}
                    onChange={(e) => setAiOptions({...aiOptions, slideCount: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
            
            {/* Submit buttons */}
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 mr-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isGenerating}
                className={`px-4 py-2 bg-blue-600 text-white font-medium rounded-md ${
                  isGenerating
                    ? 'opacity-75 cursor-not-allowed'
                    : 'hover:bg-blue-700'
                }`}
              >
                {isGenerating
                  ? 'Generating...'
                  : activeTab === 'blank'
                    ? 'Create Deck'
                    : activeTab === 'business-plan'
                      ? 'Generate from Business Plan'
                      : 'Generate with AI'
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
